import { QuaggaJSCodeReader } from "@ericblade/quagga2";
import * as Yup from "yup";
import { BarcodeReaderInfo, ReaderData, ScannerSettingsItem } from "./types";
import { Transitions } from "../finite-state-machine";

export const BarcodeReaders: Record<string, BarcodeReaderInfo> = {
  code_128_reader: {
    value: "code_128_reader",
    label: "Code 128",
    format: "code_128",
  },
  ean_reader: {
    value: "ean_reader",
    format: "ean_13",
    label: "EAN",
  },
  ean_5_reader: {
    value: "ean_5_reader",
    label: "EAN-5",
    format: "ean_5",
    supplementOf: "ean_reader",
  },
  ean_2_reader: {
    value: "ean_2_reader",
    label: "EAN-2",
    format: "ean_2",
    supplementOf: "ean_reader",
  },
  ean_8_reader: { value: "ean_8_reader", label: "EAN-8", format: "ean_8" },
  code_39_reader: {
    value: "code_39_reader",
    label: "Code 39",
    format: "code_39",
  },
  code_39_vin_reader: {
    value: "code_39_vin_reader",
    label: "Code 39 VIN",
    format: "code_39_vin",
  },
  codabar_reader: {
    value: "codabar_reader",
    label: "Codabar",
    format: "codabar",
  },
  upc_reader: { value: "upc_reader", label: "UPC", format: "upc_a" },
  upc_e_reader: { value: "upc_e_reader", label: "UPC-E", format: "upc_e" },
  i2of5_reader: {
    value: "i2of5_reader",
    label: "Interleaved Two of Five",
    format: "i2of5",
  },
  ["2of5_reader"]: {
    value: "2of5_reader",
    label: "Two of Five",
    format: "2of5",
  },
  code_93_reader: {
    value: "code_93_reader",
    label: "Code 93",
    format: "code_39",
  },
  code_32_reader: {
    value: "code_32_reader",
    label: "Code 32",
    format: "code_32",
  },
  qrcode_reader: {
    value: "qrcode_reader",
    label: "QR Code",
    format: "qr_code",
  },
} as const;

export const HORIZONTAL_PADDING_FACTOR = 0.05;
export const DEFAULT_ASPECT_RATIO = 1.6;
export const DEFAULT_MAX_WIDTH = 300;

export const DIMMED_COLOR = "rgba(0,0,0,0.5)";
export const BRACKET_SIZE = 50;
export const BRACKET_THICKNESS = 4;

export const barcodeSettingsStorageKey = "__BARCODE_SETTINGS__";

export const RE_SCAN_DELAY = 1000;

export const defaultReaders: ReaderData = {
  upc_reader: {
    position: 0,
    value: "upc_reader",
    enabled: true,
  },
  ean_reader: {
    position: 1,
    value: "ean_reader",
    enabled: true,
  },
  ean_5_reader: {
    position: 2,
    value: "ean_5_reader",
    enabled: true,
  },
  ean_2_reader: {
    position: 3,
    value: "ean_2_reader",
    enabled: true,
  },
  ean_8_reader: {
    position: 4,
    value: "ean_8_reader",
    enabled: true,
  },
  i2of5_reader: {
    position: 5,
    value: "i2of5_reader",
    enabled: true,
  },
  code_128_reader: {
    position: 6,
    value: "code_128_reader",
    enabled: true,
  },
  code_39_reader: {
    position: 7,
    value: "code_39_reader",
    enabled: true,
  },
} as ReaderData;

const scannerSettingsBaseItemSchema = Yup.object({
  position: Yup.number(),
  enabled: Yup.boolean(),
}).required();

export const QuaggaScannerSettingsItemSchema = scannerSettingsBaseItemSchema
  .shape({
    value: Yup.string(),
  })
  .required();

export const yupObjectOf = <T extends Yup.AnySchema>(schema: T) => ({
  name: "objectOf",
  exclusive: false,
  message: "Object values don't match the given schema",
  test: (value: null | Record<string, unknown>) => {
    return (
      value === null ||
      Object.values(value).every(() => schema.isValidSync(value))
    );
  },
});

export const QuaggaScannerSettingsItemsSchema = yupObjectOf(
  QuaggaScannerSettingsItemSchema
);

export const defaultConstraints = {
  width: 640,
  height: 480,
};

export const locatorConfig = {
  debug: {
    showCanvas: true,
    showPatches: true,
    showFoundPatches: true,
    showSkeleton: true,
    showLabels: true,
    showPatchLabels: true,
    showRemainingPatchLabels: true,
    boxFromPatches: {
      showTransformed: true,
      showTransformedBox: true,
      showBB: true,
    },
  },
};

export const debugConfig = {
  drawBoundingBox: true,
  showFrequency: true,
  drawScanline: true,
  showPattern: true,
};

export enum ScannerState {
  STOPPED,
  RUNNING,
  PAUSED,
}

export const scannerStateTransitions: Transitions<ScannerState> = {
  [ScannerState.STOPPED]: {
    to: [ScannerState.RUNNING],
  },
  [ScannerState.RUNNING]: {
    to: [ScannerState.PAUSED, ScannerState.STOPPED],
  },
  [ScannerState.PAUSED]: { to: [ScannerState.RUNNING, ScannerState.STOPPED] },
};
